/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
import { createAppStore } from '@marriott/mi-store-utils';
import { produce } from 'immer';

/**
 * Call status type for tracking GraphQL query states
 * Only used for amenities which is not managed by SWR
 */
export type HQVCallStatus = 'idle' | 'loading' | 'success' | 'error';

/**
 * Datalayer call status type
 */
export type DatalayerCallStatus = 'pending' | 'called';

/**
 * HQV Fallback mode type - determines how HQV should be displayed
 * - 'normal': All calls succeeded, display full HQV
 * - 'amenities_only': PropertyInfo and PhotoGallery failed, show only amenities
 * - 'closed': All calls failed, modal should be closed
 */
export type HQVFallbackMode = 'normal' | 'amenities_only' | 'closed';

/**
 * Property Info status - tracks if PropertyInfo call specifically failed
 * Used to hide Location and Overview sections when PropertyInfo fails
 * (even if PhotoGallery succeeds)
 */
export type PropertyInfoStatus = 'idle' | 'success' | 'error';

/**
 * Interface for HQV Datalayer Tracking store state
 * Simplified to only track what SWR doesn't handle:
 * - amenitiesCallStatus: Amenities call is not managed by SWR
 * - datalayerCallStatus: Tracks if datalayer has been called
 * - hqvFallbackMode: Final fallback mode after retry logic
 * - retryAttempted: Tracks if retry has been attempted (managed by orchestration hook)
 * - photoViewerLoaded: Tracks if photo viewer has loaded
 * - propertyInfoStatus: Tracks if PropertyInfo call succeeded or failed (for hiding Location/Overview)
 */
export interface HQVDatalayerTrackingState {
  amenitiesCallStatus: HQVCallStatus;
  datalayerCallStatus: DatalayerCallStatus;
  hqvFallbackMode: HQVFallbackMode;
  retryAttempted: boolean;
  photoViewerLoaded: boolean;
  propertyInfoStatus: PropertyInfoStatus;
}

/**
 * Interface for HQV Datalayer Tracking store actions
 */
export interface HQVDatalayerTrackingActions {
  setAmenitiesCallStatus: (status: HQVCallStatus) => void;
  setDatalayerCallStatus: (status: DatalayerCallStatus) => void;
  setHqvFallbackMode: (mode: HQVFallbackMode) => void;
  setRetryAttempted: (attempted: boolean) => void;
  setPhotoViewerLoaded: (loaded: boolean) => void;
  setPropertyInfoStatus: (status: PropertyInfoStatus) => void;
  resetStore: () => void;
}

/**
 * Combined store type
 */
export type HQVDatalayerTrackingStore = HQVDatalayerTrackingState & HQVDatalayerTrackingActions;

/**
 * Initial state for the store
 */
const initialState: HQVDatalayerTrackingState = {
  amenitiesCallStatus: 'idle',
  datalayerCallStatus: 'pending',
  hqvFallbackMode: 'normal',
  retryAttempted: false,
  photoViewerLoaded: false,
  propertyInfoStatus: 'idle',
};

/**
 * HQV Datalayer Tracking Zustand store creator
 * Simplified to track only what SWR doesn't handle:
 * - Amenities call status (not managed by SWR)
 * - Datalayer call status
 * - HQV fallback mode (set by orchestration hook)
 * - Retry attempted flag
 * - Photo viewer loaded flag
 */
const hqvDatalayerTrackingStoreCreator: StateCreator<HQVDatalayerTrackingStore> = set => {
  return {
    ...initialState,

    setAmenitiesCallStatus: (status: HQVCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.amenitiesCallStatus = status;
        })
      );
    },

    setDatalayerCallStatus: (status: DatalayerCallStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.datalayerCallStatus = status;
        })
      );
    },

    setHqvFallbackMode: (mode: HQVFallbackMode) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.hqvFallbackMode = mode;
        })
      );
    },

    setRetryAttempted: (attempted: boolean) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.retryAttempted = attempted;
        })
      );
    },

    setPhotoViewerLoaded: (loaded: boolean) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.photoViewerLoaded = loaded;
        })
      );
    },

    setPropertyInfoStatus: (status: PropertyInfoStatus) => {
      set(
        produce((draft: HQVDatalayerTrackingStore) => {
          draft.propertyInfoStatus = status;
        })
      );
    },

    resetStore: () => {
      set({ ...initialState });
    },
  };
};

/**
 * Create the HQV Datalayer Tracking store using the existing createAppStore pattern
 */
export const useHQVDatalayerTrackingStore = createAppStore(hqvDatalayerTrackingStoreCreator, {
  usePersistentStore: false,
});
